import { customElement, html, DeesElement, property, css, cssManager, type TemplateResult } from '@design.estate/dees-element';
import { demoFunc } from './dees-pagination.demo.js';
import { themeDefaultStyles } from '../../00theme.js';

declare global {
  interface HTMLElementTagNameMap {
    'dees-pagination': DeesPagination;
  }
}

/**
 * A simple pagination component.
 * @fires page-change - Emitted when the page is changed. detail: { page: number }
 */
@customElement('dees-pagination')
export class DeesPagination extends DeesElement {
  public static demo = demoFunc;
  public static demoGroups = ['Layout'];
  /** Current page (1-based) */
  @property({ type: Number, reflect: true })
  accessor page = 1;

  /** Total number of pages */
  @property({ type: Number, reflect: true })
  accessor total = 1;

  public static styles = [
    themeDefaultStyles,
    cssManager.defaultStyles,
    css`
      :host {
        display: inline-flex;
        align-items: center;
        gap: 2px;
        font-family: var(--dees-font-family);
      }
      button {
        background: none;
        border: none;
        min-width: var(--dees-control-height-sm);
        height: var(--dees-control-height-sm);
        padding: 0 6px;
        font-size: var(--dees-font-control-size);
        font-variant-numeric: tabular-nums;
        cursor: pointer;
        color: var(--dees-color-text-secondary);
        border-radius: var(--dees-radius-md);
        corner-shape: var(--dees-corner-shape);
        transition:
          background-color var(--dees-transition-fast) var(--dees-ease-standard),
          color var(--dees-transition-fast) var(--dees-ease-standard);
      }
      button:hover:not(:disabled) {
        background: var(--dees-color-hover);
        color: var(--dees-color-text-primary);
      }
      button:active:not(:disabled) {
        background: var(--dees-color-active);
      }
      button:focus-visible {
        outline: none;
        box-shadow: 0 0 0 3px var(--dees-color-focus-ring);
      }
      button:disabled {
        cursor: default;
        color: var(--dees-color-label-quaternary);
      }
      button.current {
        background: var(--dees-color-accent-primary);
        color: var(--dees-color-on-accent);
        font-weight: 600;
        cursor: default;
      }
      span.ellipsis {
        margin: 0 4px;
        color: var(--dees-color-text-muted);
      }
    `,
  ];

  private get pages(): (number | string)[] {
    const pages: (number | string)[] = [];
    const total = this.total;
    const current = this.page;
    if (total <= 7) {
      for (let i = 1; i <= total; i++) {
        pages.push(i);
      }
    } else {
      pages.push(1);
      if (current > 4) {
        pages.push('...');
      }
      const start = Math.max(2, current - 2);
      const end = Math.min(total - 1, current + 2);
      for (let i = start; i <= end; i++) {
        pages.push(i);
      }
      if (current < total - 3) {
        pages.push('...');
      }
      pages.push(total);
    }
    return pages;
  }

  public render(): TemplateResult {
    return html`
      <button
        @click=${() => this.changePage(this.page - 1)}
        ?disabled=${this.page <= 1}
        aria-label="Previous page"
      >
        ‹
      </button>
      ${this.pages.map((p) =>
        p === '...'
          ? html`<span class="ellipsis">…</span>`
          : html`
              <button
                class="${p === this.page ? 'current' : ''}"
                @click=${() => this.changePage(p as number)}
                ?disabled=${p === this.page}
                aria-label="Page ${p}"
              >
                ${p}
              </button>
            `
      )}
      <button
        @click=${() => this.changePage(this.page + 1)}
        ?disabled=${this.page >= this.total}
        aria-label="Next page"
      >
        ›
      </button>
    `;
  }

  private changePage(newPage: number) {
    if (newPage < 1 || newPage > this.total || newPage === this.page) {
      return;
    }
    this.page = newPage;
    this.dispatchEvent(
      new CustomEvent('page-change', {
        detail: { page: this.page },
        bubbles: true,
      })
    );
  }
}